import { logger } from '@springtree/eva-sdk-core-logger';
import { atom, selector } from 'recoil';
import storage from '../helpers/storage.js';
// Import the 2 root state atoms
// We also need the current user response to determine the type of user
//
import currentUserState from '../state/current-user/current-user-state.js';
import currentUserTokenState from '../state/current-user/current-user-token-state.js';
import evaEndpointUrlState from '../state/core/eva-endpoint-url-state.js';
import requestedOrganizationUnitIdState from '../state/core/requested-organization-unit-id-state.js';
import requestedOrganizationUnitQueryState from '../state/core/requested-organization-unit-query-state.js';
import getEvaEndpoint from '../helpers/get-eva-endpoint.js';
import userExpiration from '../helpers/user-expiration.js';
/**
 * Build the state values for an EVA service call.
 * As a bare minimum you need to supply the service definition from the SDK.
 *
 * The recoil `key` needs to be unique and will default to the service name.
 * If you require the same service for different use cases you must supply a
 * unique key name which should also indicate its intended purpose.
 * As a convenience you can use the `requireCustomer` and `requireEmployee`
 * flags to prevent service calls from going out depending on the current user.
 *
 * @template SVC
 * @param {{
 *     allowEmptyRequest?: boolean,
 *     service: new () => SVC,
 *     defaultRequest?: SVC['request'],
 *     key?: string,
 *     requireCustomer?: boolean,
 *     requireEmployee?: boolean,
 *     requireLoggedIn?: boolean,
 *     beforeRequest?: (params: { req: SVC['request'], get: GetRecoilValue }) => SVC['request'],
 *     requestedOrganizationUnitIDOverrideState: RecoilState<number>,
 *   }} {
 *     allowEmptyRequest = true,
 *     service,
 *     defaultRequest,
 *     key,
 *     requireCustomer,
 *     requireEmployee,
 *     requireLoggedIn,
 *     beforeRequest,
 *     requestedOrganizationUnitIDOverrideState,
 *   }
 * @returns {IServiceState<SVC>}
 */
const buildServiceState = ({ allowEmptyRequest = true, service, defaultRequest, key, requireCustomer, requireEmployee, requireLoggedIn, beforeRequest, customRequest, requestedOrganizationUnitIDOverrideState, }) => {
    // Use provided key but default from service
    //
    const recoilKey = key || new service().name;
    // The stale state is used to manipulate the memoized function behavior of recoil
    // A date/time string is used as the request ID.
    // See: https://recoiljs.org/docs/guides/asynchronous-data-queries#use-a-request-id
    //
    const staleState = atom({
        key: `${recoilKey}/Stale`,
        default: `${new Date().valueOf()}`,
    });
    const requestState = atom({
        key: `${recoilKey}/Request`,
        default: selector({
            key: `${recoilKey}/Default`,
            get: ({ get }) => {
                const endpointUrl = get(evaEndpointUrlState);
                const currentUser = get(currentUserState.response);
                const userId = currentUser?.User.ID || 'default';
                const storageKey = `${endpointUrl.replace(/https?:\/\//, '')}:${userId}:${recoilKey}`;
                const storedValue = storage.getItem(storageKey);
                if (storedValue) {
                    const parsedValue = JSON.parse(storedValue);
                    return parsedValue;
                }
                return defaultRequest;
            },
        }),
    });
    // The request state can optionally be persisted to local storage
    //
    const storageState = selector({
        key: `${recoilKey}/Storage`,
        get: async ({ get }) => {
            const endpointUrl = get(evaEndpointUrlState);
            const currentUser = get(currentUserState.response);
            const requestPayload = get(requestState);
            if (!endpointUrl) {
                return;
            }
            const userId = currentUser?.User.ID || 'default';
            const storageKey = `${endpointUrl.replace(/https?:\/\//, '')}:${userId}:${recoilKey}`;
            const storageValue = JSON.stringify(requestPayload || {});
            storage.setItem(storageKey, storageValue);
        },
    });
    // The response state can be undefined (initially), the service response
    // or an error.
    // Additional selectors are available to target either the Error or response
    //
    const responseRawState = selector({
        key: `${recoilKey}/Response/Raw`,
        get: async ({ get }) => {
            const evaEndpointUrl = get(evaEndpointUrlState);
            const currentUser = get(currentUserState.response);
            const currentUserToken = get(currentUserTokenState);
            // Check if the request OrganizationUnitID is overridden, if so use that. Otherwise use the current user's OrganizationUnitID
            const evaRequestedOrganizationUnitID = get(requestedOrganizationUnitIDOverrideState ?? requestedOrganizationUnitIdState);
            const evaRequestedOrganizationUnitQuery = get(requestedOrganizationUnitQueryState);
            let requestPayload = get(requestState);
            get(staleState);
            if (!evaEndpointUrl) {
                return;
            }
            // The currentUserToken will be the first to be unset so the currentUser could in theory still have data
            // It is important to check this first
            //
            if ((!currentUserToken || !currentUser) && (requireCustomer || requireEmployee || requireLoggedIn)) {
                return;
            }
            if (requireCustomer && (!currentUser || (currentUser.User.Type & 2) === 0)) {
                return;
            }
            if (requireEmployee && (!currentUser || (currentUser.User.Type & 1) === 0)) {
                return;
            }
            if (requireLoggedIn && (!currentUser || (currentUser.User.Type & 1) === 0 && (currentUser.User.Type & 2) === 0)) {
                return;
            }
            // If the caller provided a method to modify the request call it here
            // before sending it to the back-end
            //
            if (typeof beforeRequest === 'function') {
                try {
                    requestPayload = beforeRequest({ get, req: requestPayload });
                }
                catch (error) {
                    // logger.warn(`[${recoilKey}] Request prepare call abort`, error)();
                    return error;
                }
            }
            if (!allowEmptyRequest && Object.keys(requestPayload || {}).length === 0) {
                return;
            }
            try {
                const evaEndpoint = await getEvaEndpoint(evaEndpointUrl);
                const options = {
                    authenticationToken: currentUserToken,
                    requestedOrganizationUnitID: evaRequestedOrganizationUnitID,
                    requestedOrganizationUnitQuery: evaRequestedOrganizationUnitQuery,
                };
                // Use the custom request implementation if provided. Can be used for
                // composite services are call using a custom XHR
                //
                const response = customRequest ?
                    await customRequest({ options, endpoint: evaEndpoint, payload: requestPayload })
                    : await evaEndpoint.callService(service, requestPayload, options);
                return response;
            }
            catch (error) {
                if (error.response?.status === 401) {
                    logger.warn(`[${recoilKey}] User token has expired`, error)();
                    userExpiration.tokenExpired(currentUserToken);
                }
                else {
                    logger.warn(`[${recoilKey}] Call failed`, error)();
                }
                return error;
            }
        },
    });
    const responseResultState = selector({
        key: `${recoilKey}/Response/Result`,
        get: ({ get }) => {
            const responseRaw = get(responseRawState);
            if (responseRaw && !(responseRaw instanceof Error)) {
                return responseRaw;
            }
        },
    });
    const responseErrorState = selector({
        key: `${recoilKey}/Response/Error`,
        get: ({ get }) => {
            const responseRaw = get(responseRawState);
            if (responseRaw && responseRaw instanceof Error) {
                return responseRaw;
            }
        },
    });
    const state = {
        recoilKey,
        error: responseErrorState,
        request: requestState,
        response: responseResultState,
        responseRaw: responseRawState,
        stale: staleState,
        storage: storageState,
    };
    return state;
};
export default buildServiceState;
//# sourceMappingURL=build-service-state.js.map